// v2.18.3 — Zones chaudes : Mapbox dark + heatmap GeoJSON.
// v2.18.3a — Robustesse :
//   1) Shell statique (header / chips / légende / overlay stats) TOUJOURS rendu,
//      hors du render path Mapbox → s'affiche même si WebGL plante.
//   2) Fallback Leaflet (CircleMarker) si mapboxgl.supported() === false.
//   3) Loader/message dégradé avec timeout 2.5s.

import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { MapContainer, TileLayer, CircleMarker } from "react-leaflet";
import "leaflet/dist/leaflet.css";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN || "";

const WINDOWS = [
  { id: 30, label: "30 jours" },
  { id: 90, label: "90 jours" },
  { id: 365, label: "365 jours" },
];

function detectMapboxSupport() {
  try {
    return typeof mapboxgl !== "undefined" && typeof mapboxgl.supported === "function" && mapboxgl.supported({ failIfMajorPerformanceCaveat: false });
  } catch (_) { return false; }
}

// ── Sous-composant : carte Mapbox (rendu uniquement si supporté). ────────────
function MapboxHeatLayer({ token, geojson, onReadyChange }) {
  const containerRef = useRef(null);
  const mapRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return undefined;
    if (!token) return undefined;
    let cancelled = false;
    try {
      mapboxgl.accessToken = token;
      const map = new mapboxgl.Map({
        container: containerRef.current,
        style: "mapbox://styles/mapbox/dark-v11",
        center: [-4.024, 5.345],
        zoom: 11,
        attributionControl: false,
      });
      map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), "top-left");
      map.on("load", () => {
        if (cancelled) return;
        map.addSource("heat", { type: "geojson", data: { type: "FeatureCollection", features: [] } });
        map.addLayer({
          id: "heat-layer",
          type: "heatmap",
          source: "heat",
          maxzoom: 18,
          paint: {
            "heatmap-weight": ["coalesce", ["get", "weight"], 1],
            "heatmap-intensity": ["interpolate", ["linear"], ["zoom"], 0, 1, 15, 3],
            "heatmap-radius": ["interpolate", ["linear"], ["zoom"], 0, 2, 15, 20],
            "heatmap-color": [
              "interpolate", ["linear"], ["heatmap-density"],
              0, "rgba(33,102,172,0)",
              0.2, "rgb(103,169,207)",
              0.4, "rgb(102,194,165)",
              0.6, "rgb(253,231,37)",
              0.8, "rgb(252,141,89)",
              1, "rgb(215,25,28)",
            ],
            "heatmap-opacity": 0.78,
          },
        });
        mapRef.current = map;
        onReadyChange && onReadyChange(true);
      });
      map.on("error", () => onReadyChange && onReadyChange(false, "mapbox_error"));
    } catch (e) {
      onReadyChange && onReadyChange(false, e.message);
    }
    return () => {
      cancelled = true;
      if (mapRef.current) {
        try { mapRef.current.remove(); } catch (_) {}
        mapRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Push geojson into the source when geojson changes.
  useEffect(() => {
    if (!mapRef.current || !geojson) return;
    const apply = () => {
      const src = mapRef.current && mapRef.current.getSource("heat");
      if (src) src.setData(geojson);
    };
    if (mapRef.current.isStyleLoaded()) apply();
    else mapRef.current.once("load", apply);
  }, [geojson]);

  return <div ref={containerRef} data-testid="heatmap-mapbox-container" style={{ position: "absolute", inset: 0 }} />;
}

// ── Sous-composant : fallback Leaflet (CircleMarker). ────────────────────────
function LeafletHeatFallback({ geojson }) {
  const features = (geojson && geojson.features) || [];
  return (
    <div data-testid="heatmap-leaflet-fallback" style={{ position: "absolute", inset: 0 }}>
      <MapContainer center={[5.345, -4.024]} zoom={11} style={{ width: "100%", height: "100%", background: "#0F172A" }} attributionControl={false}>
        <TileLayer
          attribution='&copy; OpenStreetMap'
          url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
        />
        {features.map((f, i) => {
          const c = f.geometry && f.geometry.coordinates;
          if (!Array.isArray(c) || c.length < 2) return null;
          const w = Number(f.properties && f.properties.weight) || 0.3;
          return (
            <CircleMarker
              key={i}
              center={[c[1], c[0]]}
              radius={Math.max(2, 8 * w)}
              pathOptions={{
                color: "#ef4444",
                fillColor: "#ef4444",
                fillOpacity: 0.35,
                stroke: false,
                weight: 0,
                className: "civ-ai-heat-circle",
              }}
            />
          );
        })}
      </MapContainer>
    </div>
  );
}

// ── Composant principal. ─────────────────────────────────────────────────────
export default function CourierHeatmapPage() {
  const [courier, setCourier] = useState(null);
  const [status, setStatus] = useState("checking");
  const [days, setDays] = useState(365);
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState(null);
  const [geojson, setGeojson] = useState({ type: "FeatureCollection", features: [] });
  const [mapboxOk, setMapboxOk] = useState(null); // null=checking, true=ok, false=falling-back
  const [mapboxReady, setMapboxReady] = useState(false);

  // Detect support immediately.
  useEffect(() => {
    const ok = detectMapboxSupport() && !!MAPBOX_TOKEN;
    setMapboxOk(ok);
  }, []);

  // Watchdog : si Mapbox ne signale pas "ready" en 4s, on tombe en fallback.
  useEffect(() => {
    if (mapboxOk !== true || mapboxReady) return undefined;
    const t = setTimeout(() => {
      if (!mapboxReady) setMapboxOk(false);
    }, 4000);
    return () => clearTimeout(t);
  }, [mapboxOk, mapboxReady]);

  // Auth.
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/v3/courier/me`, { withCredentials: true });
        if (!r.data?.ok) throw new Error("no_courier");
        setCourier(r.data.courier);
        setStatus("authorized");
      } catch (_) { setStatus("denied"); }
    })();
  }, []);

  useEffect(() => {
    if (status === "denied") {
      window.location.replace("/livreur/login?next=%2Flivreur%2Fzones-chaudes");
    }
  }, [status]);

  // Load heatmap data when days/auth changes.
  useEffect(() => {
    if (status !== "authorized") return undefined;
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/v3/courier/stats/heatmap?days=${days}`, { withCredentials: true });
        if (cancelled) return;
        setGeojson(r.data || { type: "FeatureCollection", features: [] });
        setMeta(r.data?.meta || null);
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.error || e.message);
      }
    })();
    return () => { cancelled = true; };
  }, [days, status]);

  const onLogout = async () => {
    try { await axios.post(`${API}/v3/courier/logout`, {}, { withCredentials: true }); } catch (_) {}
    window.location.replace("/livreur/login");
  };

  const onMapboxReadyChange = (ready, reason) => {
    if (ready) setMapboxReady(true);
    else if (reason) setMapboxOk(false);
  };

  const degraded = useMemo(() => mapboxOk === false, [mapboxOk]);

  // Static shell — JAMAIS conditionné par le statut Mapbox.
  return (
    <div data-testid="courier-heatmap-page" style={{ position: "relative", width: "100%", height: "100vh", background: "#0F172A", overflow: "hidden", fontFamily: "Inter, system-ui, sans-serif" }}>
      {/* Carte (Mapbox si supporté, sinon Leaflet) */}
      {mapboxOk === true && (
        <MapboxHeatLayer token={MAPBOX_TOKEN} geojson={geojson} onReadyChange={onMapboxReadyChange} />
      )}
      {mapboxOk === false && (
        <LeafletHeatFallback geojson={geojson} />
      )}
      {mapboxOk === null && (
        <div data-testid="heatmap-loader" style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", color: "#94A3B8", fontSize: 13 }}>
          Initialisation de la carte…
        </div>
      )}

      {/* Header overlay — statique. */}
      <header style={S.header}>
        <a href="/livreur/historique" data-testid="back-to-historique-btn" style={S.backBtn}>← Historique</a>
        <div style={S.headerCenter}>
          <span style={S.proBadge}>PRO</span>
          <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.1 }}>
            <span style={S.headerName}>{status === "authorized" ? (courier?.courier_name || "—") : "Chargement…"}</span>
            <span style={S.headerCompany}>{courier?.company_name || ""}</span>
          </div>
        </div>
        <button type="button" data-testid="logout-btn" onClick={onLogout} style={S.logoutBtn}>⎋</button>
      </header>

      {/* Window chips — statiques. */}
      <div data-testid="window-chips" style={S.chipsWrap}>
        {WINDOWS.map((w) => (
          <button
            type="button"
            key={w.id}
            data-testid={`chip-${w.id}`}
            onClick={() => setDays(w.id)}
            style={{ ...S.chip, ...(w.id === days ? S.chipActive : null) }}
          >{w.label}</button>
        ))}
      </div>

      {/* Degraded mode banner — statique, visible si Mapbox indispo. */}
      {degraded && (
        <div data-testid="degraded-banner" style={S.degradedBanner}>
          ⚠️ Mode dégradé : carte simplifiée (votre navigateur ne supporte pas la 3D). Toutes les interactions restent fonctionnelles.
        </div>
      )}

      {/* Bottom stats + legend — statiques. */}
      <div data-testid="heatmap-meta" style={S.bottom}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
          <span style={{ fontSize: 12, color: "#E2E8F0" }}>
            {meta ? `${meta.courses_count} courses · ${meta.points_count} points` : "Aucune donnée"}
            <span style={{ color: "#94A3B8" }}> · Période : {days}j</span>
            {meta?.sampled && <span style={{ color: "#FBBF24", marginLeft: 8 }}>(échantillonné)</span>}
          </span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 6 }}>
          <span data-testid="legend-low" style={{ fontSize: 9, color: "#94A3B8" }}>Faible</span>
          <div style={S.legendBar} />
          <span data-testid="legend-high" style={{ fontSize: 9, color: "#94A3B8" }}>Élevé</span>
        </div>
      </div>

      {error && (
        <div data-testid="heatmap-error" style={{ ...S.bottom, top: 110, bottom: "auto", background: "rgba(239,68,68,0.18)", borderColor: "rgba(239,68,68,0.6)" }}>
          <span style={{ fontSize: 12, color: "#FCA5A5" }}>Erreur : {error}</span>
        </div>
      )}
    </div>
  );
}

const S = {
  header: {
    position: "absolute", top: 12, left: 12, right: 12, zIndex: 10,
    display: "flex", alignItems: "center", gap: 10,
    padding: "8px 12px",
    background: "rgba(15, 23, 42, 0.92)",
    border: "1px solid rgba(245, 158, 11, 0.5)",
    borderRadius: 999,
    boxShadow: "0 6px 22px rgba(0,0,0,0.45)",
    backdropFilter: "blur(10px)",
  },
  backBtn: { color: "#CBD5E1", textDecoration: "none", fontSize: 12, padding: "5px 9px", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 999 },
  headerCenter: { display: "flex", alignItems: "center", gap: 8, flex: 1, justifyContent: "center" },
  proBadge: { background: "linear-gradient(135deg, #F59E0B, #FBBF24)", color: "#0F172A", fontSize: 10, fontWeight: 800, padding: "2px 7px", borderRadius: 999 },
  headerName: { fontSize: 12, fontWeight: 700, color: "#F8FAFC" },
  headerCompany: { fontSize: 9, color: "#94A3B8" },
  logoutBtn: { background: "rgba(239,68,68,0.14)", border: "1px solid rgba(239,68,68,0.4)", color: "#FCA5A5", fontSize: 13, padding: "4px 9px", borderRadius: 999, cursor: "pointer" },
  chipsWrap: {
    position: "absolute", top: 64, left: "50%", transform: "translateX(-50%)",
    display: "flex", gap: 6, zIndex: 10,
    background: "rgba(15, 23, 42, 0.78)",
    padding: 4, borderRadius: 999, border: "1px solid rgba(255,255,255,0.08)",
    backdropFilter: "blur(10px)",
  },
  chip: {
    background: "transparent", color: "#CBD5E1", border: "none",
    fontSize: 11, padding: "5px 12px", borderRadius: 999, cursor: "pointer", fontWeight: 600,
  },
  chipActive: {
    background: "linear-gradient(135deg, #F59E0B, #FBBF24)", color: "#0F172A",
  },
  degradedBanner: {
    position: "absolute", top: 110, left: 12, right: 12, zIndex: 10,
    padding: "8px 12px",
    background: "rgba(251, 191, 36, 0.18)",
    border: "1px solid rgba(251, 191, 36, 0.5)",
    color: "#FDE68A",
    borderRadius: 10,
    fontSize: 11,
    lineHeight: 1.35,
    backdropFilter: "blur(8px)",
  },
  bottom: {
    position: "absolute", left: 12, right: 12, bottom: 12, zIndex: 10,
    padding: "10px 14px",
    background: "rgba(15, 23, 42, 0.88)",
    border: "1px solid rgba(255,255,255,0.1)",
    borderRadius: 12,
    backdropFilter: "blur(10px)",
    boxShadow: "0 10px 28px rgba(0,0,0,0.5)",
  },
  legendBar: {
    flex: 1, height: 7, borderRadius: 4,
    background: "linear-gradient(90deg, rgba(33,102,172,0.4), rgb(103,169,207), rgb(102,194,165), rgb(253,231,37), rgb(252,141,89), rgb(215,25,28))",
  },
};
